import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_CUTOFF_MINUTES, DEFAULT_WINDOWS } from '../core/constants/windows';
import { DEFAULT_PER_NUMBER_CAP } from '../core/constants/plays';
import { sanitizePayoutMultipliersPatch } from '../core/rules/payoutMultipliers';
import { resolveWindowSchedule } from '../core/rules/windowSchedule';
import { useDataSync } from './useDataSync';
import type { HouseConfig } from '../types';

const DEFAULT_CONFIG: HouseConfig = {
  dayOpen: DEFAULT_WINDOWS.day.open,
  dayClose: DEFAULT_WINDOWS.day.close,
  nightOpen: DEFAULT_WINDOWS.night.open,
  nightClose: DEFAULT_WINDOWS.night.close,
  cutoffMin: DEFAULT_CUTOFF_MINUTES,
  perNumberCap: DEFAULT_PER_NUMBER_CAP,
  source: 'florida',
  routeDepartMode: 'manual',
  routeDepartAt: '18:00',
  routeDepartMarginMin: 15,
  routeIdleAlertMin: 10,
  runnerMaxLoadAmount: 1_000_000,
};

function normalizeConfigSource(raw: unknown): Partial<HouseConfig> | null {
  if (!raw) return null;
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row || typeof row !== 'object') return null;
  const doc = row as Record<string, unknown>;
  if (!doc.dayClose && !doc.nightClose && !doc.cutoffMin && !doc.perNumberCap) return null;
  const resolved = resolveWindowSchedule({
    dayOpen: doc.dayOpen != null ? String(doc.dayOpen) : undefined,
    dayClose: doc.dayClose != null ? String(doc.dayClose) : undefined,
    nightOpen: doc.nightOpen != null ? String(doc.nightOpen) : undefined,
    nightClose: doc.nightClose != null ? String(doc.nightClose) : undefined,
    cutoffMin: doc.cutoffMin != null ? Number(doc.cutoffMin) : undefined,
  });
  return {
    dayOpen: resolved.dayOpen,
    dayClose: resolved.dayClose,
    nightOpen: resolved.nightOpen,
    nightClose: resolved.nightClose,
    cutoffMin: resolved.cutoffMin,
    perNumberCap: Number(doc.perNumberCap ?? DEFAULT_CONFIG.perNumberCap),
    source: String(doc.source ?? DEFAULT_CONFIG.source),
    numberCaps: doc.numberCaps && typeof doc.numberCaps === 'object'
      ? Object.fromEntries(
        Object.entries(doc.numberCaps as Record<string, unknown>)
          .map(([k, v]) => [k, Number(v)]),
      )
      : undefined,
    surgePlayThreshold: doc.surgePlayThreshold != null
      ? Number(doc.surgePlayThreshold)
      : undefined,
    surgeCapRatio: doc.surgeCapRatio != null ? Number(doc.surgeCapRatio) : undefined,
    autoFloridaSync: doc.autoFloridaSync === false ? false : undefined,
    autoNewYorkSync: doc.autoNewYorkSync === false ? false : undefined,
    routeDepartMode: doc.routeDepartMode === 'scheduled' ? 'scheduled' : 'manual',
    routeDepartAt: doc.routeDepartAt != null ? String(doc.routeDepartAt) : undefined,
    routeDepartMarginMin: doc.routeDepartMarginMin != null
      ? Math.max(0, Number(doc.routeDepartMarginMin))
      : undefined,
    routeIdleAlertMin: doc.routeIdleAlertMin != null
      ? Math.max(1, Math.min(120, Number(doc.routeIdleAlertMin)))
      : undefined,
    runnerMaxLoadAmount: doc.runnerMaxLoadAmount != null
      ? Math.max(1, Math.floor(Number(doc.runnerMaxLoadAmount)))
      : undefined,
    payoutMultipliers: sanitizePayoutMultipliersPatch(doc.payoutMultipliers),
  };
}

/**
 * House config derived from remote adapter snapshots.
 * Local override is only kept until the next remote update (edit screens use their own draft).
 */
import type { IDataAdapter } from '../adapters/data/IDataAdapter';

export function useHouseConfig(adapter: IDataAdapter | null | undefined) {
  const remote = useDataSync(adapter, 'config', []);
  const remoteConfig = useMemo(() => {
    const next = normalizeConfigSource(remote);
    return next ? { ...DEFAULT_CONFIG, ...next } : DEFAULT_CONFIG;
  }, [remote]);
  const [localOverride, setLocalOverride] = useState<HouseConfig | null>(null);

  useEffect(() => {
    setLocalOverride(null);
  }, [remote]);

  const setConfig = useCallback((updater: HouseConfig | ((prev: HouseConfig) => HouseConfig)) => {
    setLocalOverride((prev) => {
      const base = prev ?? remoteConfig;
      return typeof updater === 'function' ? updater(base) : updater;
    });
  }, [remoteConfig]);

  return [localOverride ?? remoteConfig, setConfig] as const;
}
